/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpStatusCode,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import {
  catchError,
  firstValueFrom,
  from,
  switchMap,
  throwError,
  fromEvent,
  merge,
  take,
} from "rxjs";
import { environment } from "../../environments/environment";
import { KioskSetupService } from "../services/kiosk-setup.service";
import {
  KioskLocalStorageKeysEnum,
  LocalStorageService,
} from "../services/local-storage.service";

// Skip token refresh for these endpoints to prevent infinite loops
const SKIP_REFRESH_URLS = [
  "/v1/kiosk/pairingSessions",
  "/v1/kiosk/auth/accessToken",
];

const AUTH_TOKEN_URL = "/v1/kiosk/auth/accessToken";
const MAX_REFRESH_ATTEMPTS = 10;

export const kioskTokenInterceptor: HttpInterceptorFn = (req, next) => {
  const localStorageService = inject(LocalStorageService);
  const kioskSetupService = inject(KioskSetupService);
  const router = inject(Router);

  // Prepend API URL if needed
  let updatedReq = !req.url.startsWith("http")
    ? req.clone({ url: `${environment.apiURL}${req.url}` })
    : req;

  // Handle token requests with refresh token
  if (updatedReq.url.includes(AUTH_TOKEN_URL)) {
    const refreshToken = localStorageService.getItem(
      KioskLocalStorageKeysEnum.REFRESH_TOKEN,
    );
    if (refreshToken) {
      updatedReq = updatedReq.clone({
        headers: updatedReq.headers.set(
          "Authorization",
          `Bearer ${refreshToken}`,
        ),
      });
    }
    return next(updatedReq);
  }

  // Add auth token if available
  const idToken = localStorageService.getItem(
    KioskLocalStorageKeysEnum.ID_TOKEN,
  );

  if (idToken) {
    updatedReq = updatedReq.clone({
      headers: updatedReq.headers.set("Authorization", `Bearer ${idToken}`),
    });
  }

  return next(updatedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const shouldRefresh =
        error.status === HttpStatusCode.Unauthorized ||
        error.status === HttpStatusCode.Forbidden;
      const isRefreshable = !SKIP_REFRESH_URLS.some((url) =>
        updatedReq.url.includes(url),
      );

      if (shouldRefresh && isRefreshable) {
        const refreshAttempts = Number(
          localStorageService.getItem(
            KioskLocalStorageKeysEnum.REFRESH_ATTEMPTS,
          ) || "0",
        );

        // Only navigate to setup if online and exceeded max attempts
        if (navigator.onLine && refreshAttempts >= MAX_REFRESH_ATTEMPTS) {
          localStorageService.removeItem(
            KioskLocalStorageKeysEnum.REFRESH_ATTEMPTS,
          );
          localStorageService.clearTokens();
          void router.navigate(["/setup"]);
          return throwError(() => error);
        }

        if (!navigator.onLine) {
          // Wait for online event before attempting refresh
          return merge(
            fromEvent(window, "online"),
            navigator.onLine ? from([true]) : from([]),
          ).pipe(
            take(1),
            switchMap(() =>
              from(refreshToken(kioskSetupService, localStorageService)),
            ),
            switchMap((success) => {
              if (!success) {
                return throwError(() => error);
              }

              const newIdToken = localStorageService.getItem(
                KioskLocalStorageKeysEnum.ID_TOKEN,
              );
              return next(
                updatedReq.clone({
                  headers: updatedReq.headers.set(
                    "Authorization",
                    `Bearer ${newIdToken}`,
                  ),
                }),
              );
            }),
          );
        }

        localStorageService.setItem(
          KioskLocalStorageKeysEnum.REFRESH_ATTEMPTS,
          String(refreshAttempts + 1),
        );

        return from(refreshToken(kioskSetupService, localStorageService)).pipe(
          switchMap((success) => {
            if (!success) {
              localStorageService.removeItem(
                KioskLocalStorageKeysEnum.REFRESH_ATTEMPTS,
              );
              localStorageService.clearTokens();
              void router.navigate(["/setup"]);
              return throwError(() => error);
            }

            // Reset refresh attempts on successful token refresh
            localStorageService.removeItem(
              KioskLocalStorageKeysEnum.REFRESH_ATTEMPTS,
            );

            const newIdToken = localStorageService.getItem(
              KioskLocalStorageKeysEnum.ID_TOKEN,
            );
            return next(
              updatedReq.clone({
                headers: updatedReq.headers.set(
                  "Authorization",
                  `Bearer ${newIdToken}`,
                ),
              }),
            );
          }),
        );
      }
      return throwError(() => error);
    }),
  );
};

async function refreshToken(
  kioskSetupService: KioskSetupService,
  localStorageService: LocalStorageService,
): Promise<boolean> {
  try {
    const response = await firstValueFrom(kioskSetupService.getAuthToken());
    if (!response?.token) return false;

    localStorageService.setItem(
      KioskLocalStorageKeysEnum.ID_TOKEN,
      response.token,
    );
    return true;
  } catch (error) {
    console.error("Token refresh failed:", error);
    return false;
  }
}
